const API_URL = process.env.REACT_APP_API_URL || 'https://api.driftwoodagri.com';

export function getTokenPayload(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

export function getUserRole() {
  return localStorage.getItem('user_role');
}

async function refreshToken() {
  const refresh_token = localStorage.getItem('refresh_token');
  if (!refresh_token) throw new Error('No refresh token');

  const res = await fetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error('Session expired');

  const { access_token, refresh_token: new_refresh, expires } = json.data;
  localStorage.setItem('access_token', access_token);
  localStorage.setItem('refresh_token', new_refresh);
  localStorage.setItem('expires', expires);
  return access_token;
}

export async function authFetch(url, options = {}) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(url, {
    ...options,
    headers: { ...options.headers, Authorization: `Bearer ${token}` },
  });

  if (res.status === 401) {
    try {
      const newToken = await refreshToken();
      const retried = await fetch(url, {
        ...options,
        headers: { ...options.headers, Authorization: `Bearer ${newToken}` },
      });
      return retried;
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('expires');
      localStorage.removeItem('user_role');
      window.location.replace('/login');
      throw new Error('Session expired');
    }
  }

  return res;
}

export async function loginUser(email, password) {
  const res = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data?.errors?.[0]?.message || 'Invalid email or password.');
  }

  const { access_token, refresh_token, expires } = data.data;

  // Get role UUID directly from the JWT payload (always available)
  const roleId = getTokenPayload(access_token)?.role;

  // Try to fetch role name via roles list (may be restricted by permissions)
  let roleName = '';
  try {
    const roleRes = await fetch(
      `${API_URL}/roles?filter[id][_eq]=${roleId}&fields[]=name`,
      { headers: { Authorization: `Bearer ${access_token}` } }
    );
    const roleData = await roleRes.json();
    roleName = roleData?.data?.[0]?.name || '';
  } catch {
    // ignore
  }

  // Fallback: read role name from /users/me (accessible to all roles)
  if (!roleName) {
    try {
      const meRes = await fetch(
        `${API_URL}/users/me?fields=role.name`,
        { headers: { Authorization: `Bearer ${access_token}` } }
      );
      const meData = await meRes.json();
      roleName = meData?.data?.role?.name || '';
    } catch {
      // ignore
    }
  }

  localStorage.setItem('access_token', access_token);
  localStorage.setItem('refresh_token', refresh_token);
  localStorage.setItem('expires', expires);
  localStorage.setItem('user_role', roleName);

  return data;
}

export async function fetchBids() {
  const res = await authFetch(`${API_URL}/items/bids?fields=*.*`);
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch bids');
  return json.data;
}

export async function fetchMyBids() {
  const res = await authFetch(
    `${API_URL}/items/bids?fields=id,bid_amount,status,date_created,load.id,load.load_id,load.pickup_location,load.delivery_location,load.pickup_date,load.delivery_deadline,load.bidding_status,load.awarded_transporter.contact,load.order.batch.commodity,load.order.batch.grade,load.order.batch.quantity,load.order.batch.unit_of_measure&filter[user_created][_eq]=$CURRENT_USER&sort=-date_created`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch bids');
  return json.data;
}

export async function fetchLoads() {
  const res = await authFetch(
    `${API_URL}/items/loads?fields=*,order.id,order.order_id,order.batch.commodity,order.batch.seller.company_name,order.buyer.company_name,bids.id,bids.bid_amount,bids.transporter.id,bids.transporter.company_name,bids.transporter.contact,awarded_transporter.id,awarded_transporter.company_name`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch loads');
  return json.data;
}

export async function fetchOpenLoads() {
  const res = await authFetch(
    `${API_URL}/items/loads?fields=*,order.id,order.order_id,order.batch.commodity,order.batch.grade,order.batch.quantity,bids.id,bids.bid_amount,bids.transporter.id,bids.transporter.company_name&filter[bidding_status][_eq]=Open`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch loads');
  return json.data;
}

export async function updateLoad(id, data) {
  const res = await authFetch(`${API_URL}/items/loads/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to update load');
  return json.data;
}

export async function createBid(data) {
  const res = await authFetch(`${API_URL}/items/bids`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to submit bid');
  return json.data;
}

export async function updateBid(id, data) {
  const res = await authFetch(`${API_URL}/items/bids/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to update bid');
  return json.data;
}

export async function updateBatchStatus(id, status) {
  const res = await authFetch(`${API_URL}/items/batches/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to update batch status');
  return json.data;
}

export async function updateOrder(id, data) {
  const res = await authFetch(`${API_URL}/items/orders/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to update order');
  return json.data;
}

export async function fetchTraderOrders() {
  const res = await authFetch(
    `${API_URL}/items/orders?fields=*,batch.*,batch.seller.*,buyer.*,buyer.contact.*,proof_of_payment.*&filter[batch][seller][contact][_eq]=$CURRENT_USER&sort=-date_created`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch orders');
  return json.data;
}

export async function fetchMyOrders() {
  const res = await authFetch(
    `${API_URL}/items/orders?fields=*,batch.*,batch.seller.*,buyer.*,proof_of_payment.*&filter[user_created][_eq]=$CURRENT_USER&sort=-date_created`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch orders');
  return json.data;
}

export async function fetchOrders() {
  const res = await authFetch(
    `${API_URL}/items/orders?fields=*,batch.*,batch.seller.*,buyer.*,proof_of_payment.*&sort=-date_created`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch orders');
  return json.data;
}

export async function uploadFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await authFetch(`${API_URL}/files`, {
    method: 'POST',
    body: formData,
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to upload file');
  return json.data.id;
}

export function getAssetUrl(fileId) {
  const token = localStorage.getItem('access_token');
  return `${API_URL}/assets/${fileId}?access_token=${token}`;
}

export async function fetchPaymentForOrder(orderId) {
  const res = await authFetch(
    `${API_URL}/items/payments?filter[order][_eq]=${orderId}&fields=*,proof_document.*&limit=1`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch payment');
  return json.data?.[0] || null;
}

export async function fetchOrderTrail({ orderId = null, loadId = null } = {}) {
  if (!orderId && !loadId) return [];

  const filters = [];
  if (orderId) filters.push(`filter[_or][${filters.length}][order][_eq]=${encodeURIComponent(orderId)}`);
  if (loadId) filters.push(`filter[_or][${filters.length}][load][_eq]=${encodeURIComponent(loadId)}`);

  const res = await authFetch(
    `${API_URL}/items/order_trail?fields=*,order.id,load.id,user_created.first_name,user_created.last_name,user_created.email,user_updated.first_name,user_updated.last_name,user_updated.email&sort=date_created&${filters.join('&')}`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch order trail');
  return json.data || [];
}

export async function fetchPayments() {
  const res = await authFetch(
    `${API_URL}/items/payments?fields=*,order.*,partner.*&sort=-date_created`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch payments');
  return json.data;
}

export async function updatePayment(id, data) {
  const res = await authFetch(`${API_URL}/items/payments/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to update payment');
  return json.data;
}

export async function createPayment(data) {
  const normalizedData = { ...data };
  if (normalizedData.amount !== undefined && normalizedData.amount !== null && normalizedData.amount !== '') {
    const amount = parseFloat(normalizedData.amount);
    if (!Number.isNaN(amount)) {
      normalizedData.amount = amount.toFixed(2);
    }
  }

  const res = await authFetch(`${API_URL}/items/payments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(normalizedData),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to submit payment');
  return json.data;
}

export async function fetchMyPartner() {
  const res = await authFetch(
    `${API_URL}/items/partners?filter[contact][_eq]=$CURRENT_USER&limit=1`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch partner');
  return json.data?.[0] || null;
}

export async function createOrder(data) {
  const res = await authFetch(`${API_URL}/items/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to place order');
  return json.data;
}

export async function fetchBatches() {
  const res = await authFetch(
    `${API_URL}/items/batches?fields=*,seller.id,seller.company_name,seller.contact,certificates.*`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch batches');
  return json.data;
}

export async function notifyTransporters(subject, message) {
  const roleRes = await authFetch(
    `${API_URL}/roles?filter[name][_eq]=Transporter&fields[]=id`
  );
  const roleJson = await roleRes.json();
  const roleId = roleJson.data?.[0]?.id;
  if (!roleId) return;

  const usersRes = await authFetch(
    `${API_URL}/users?filter[role][_eq]=${roleId}&fields[]=id&limit=-1`
  );
  const usersJson = await usersRes.json();
  const transporterIds = (usersJson.data || []).map(u => u.id);
  if (transporterIds.length === 0) return;

  await Promise.all(
    transporterIds.map(id =>
      authFetch(`${API_URL}/notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient: id, subject, message }),
      })
    )
  );
}

export async function notifyAdmins(subject, message) {
  const roleRes = await authFetch(
    `${API_URL}/roles?filter[name][_eq]=System Admin&fields[]=id`
  );
  const roleJson = await roleRes.json();
  const roleId = roleJson.data?.[0]?.id;
  if (!roleId) return;

  const usersRes = await authFetch(
    `${API_URL}/users?filter[role][_eq]=${roleId}&fields[]=id&limit=-1`
  );
  const usersJson = await usersRes.json();
  const adminIds = (usersJson.data || []).map(u => u.id);
  if (adminIds.length === 0) return;

  await Promise.all(
    adminIds.map(id =>
      authFetch(`${API_URL}/notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient: id, subject, message }),
      })
    )
  );
}

export async function sendNotification(recipientId, subject, message) {
  const res = await authFetch(`${API_URL}/notifications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ recipient: recipientId, subject, message }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to send notification');
  return json.data;
}

export async function fetchApprovedBatches() {
  const res = await authFetch(
    `${API_URL}/items/batches?fields=*.*&filter[status][_eq]=Approved`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch batches');
  return json.data;
}

export async function createBatch(data) {
  const res = await authFetch(`${API_URL}/items/batches`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to create batch');
  return json.data;
}

export async function fetchPartners() {
  const res = await authFetch(`${API_URL}/items/partners?fields=*,contact.first_name,contact.last_name,contact.email`);
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch partners');
  return json.data;
}

export async function createPartner(data) {
  const res = await authFetch(`${API_URL}/items/partners`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to create partner');
  return json.data;
}

export async function updatePartner(id, data) {
  const res = await authFetch(`${API_URL}/items/partners/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to update partner');
  return json.data;
}

export async function deletePartner(id) {
  const res = await authFetch(`${API_URL}/items/partners/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const json = await res.json();
    throw new Error(json?.errors?.[0]?.message || 'Failed to delete partner');
  }
}

export async function fetchDisputes() {
  const res = await authFetch(
    `${API_URL}/items/disputes?fields=*,order.id,order.order_id,order.batch.commodity,order.batch.grade,order.batch.seller.company_name,order.buyer.company_name,reported_by.company_name,evidence.*&sort=-date_created`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch disputes');
  return json.data;
}

export async function updateDispute(id, data) {
  const res = await authFetch(`${API_URL}/items/disputes/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to update dispute');
  return json.data;
}

export async function fetchCurrentUser() {
  const res = await authFetch(`${API_URL}/users/me`);
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch user');
  return json.data;
}

export async function updateCurrentUser(data) {
  const res = await authFetch(`${API_URL}/users/me`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to update user');
  return json.data;
}

export async function deleteDirectusUser(id) {
  const res = await authFetch(`${API_URL}/users/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const json = await res.json();
    throw new Error(json?.errors?.[0]?.message || 'Failed to delete user');
  }
}

export async function fetchTrucks(partnerId = null) {
  const filter = partnerId
    ? `filter[partner][_eq]=${encodeURIComponent(partnerId)}`
    : 'filter[user_created][_eq]=$CURRENT_USER';
  const res = await authFetch(
    `${API_URL}/items/trucks?fields=*,driver.id,driver.name,driver.user&${filter}&sort=date_created`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch trucks');
  return json.data;
}

export async function createTruck(data) {
  const res = await authFetch(`${API_URL}/items/trucks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to create truck');
  return json.data;
}

export async function updateTruck(id, data) {
  const res = await authFetch(`${API_URL}/items/trucks/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to update truck');
  return json.data;
}

export async function deleteTruck(id) {
  const res = await authFetch(`${API_URL}/items/trucks/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const json = await res.json();
    throw new Error(json?.errors?.[0]?.message || 'Failed to delete truck');
  }
}

export async function fetchDrivers(partnerId = null) {
  const filter = partnerId
    ? `filter[partner][_eq]=${encodeURIComponent(partnerId)}`
    : 'filter[user_created][_eq]=$CURRENT_USER';
  const res = await authFetch(
    `${API_URL}/items/drivers?fields=*,user,truck.id,truck.truck_id&${filter}&sort=date_created`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch drivers');
  return json.data;
}

export async function createDirectusUser(userData) {
  const res = await authFetch(`${API_URL}/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to create user');
  return json.data;
}

export async function fetchRoleId(roleName) {
  try {
    const res = await authFetch(`${API_URL}/roles?filter[name][_eq]=${encodeURIComponent(roleName)}&fields[]=id`);
    const json = await res.json();
    return json.data?.[0]?.id || null;
  } catch {
    return null;
  }
}

export async function createDriver(data) {
  const res = await authFetch(`${API_URL}/items/drivers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to create driver');
  return json.data;
}

export async function updateDriver(id, data) {
  const res = await authFetch(`${API_URL}/items/drivers/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to update driver');
  return json.data;
}

export async function deleteDriver(id) {
  const res = await authFetch(`${API_URL}/items/drivers/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const json = await res.json();
    throw new Error(json?.errors?.[0]?.message || 'Failed to delete driver');
  }
}

export async function fetchNotifications() {
  const res = await authFetch(
    `${API_URL}/notifications?filter[recipient][_eq]=$CURRENT_USER&sort=-timestamp&limit=30`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch notifications');
  return json.data;
}

export async function markNotificationRead(id) {
  const res = await authFetch(`${API_URL}/notifications/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: 'archived' }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to update notification');
  return json.data;
}

export async function fetchMyWonBids() {
  const res = await authFetch(
    `${API_URL}/items/loads?fields=id,load_id,bidding_status,status,pickup_location,delivery_location,pickup_date,delivery_deadline,date_created,date_updated,assigned_truck.id,order.id,order.fulfillment_status,order.batch.commodity,order.batch.grade,order.batch.quantity,order.batch.unit_of_measure,bids.id,bids.bid_amount,bids.status,bids.user_created&filter[awarded_transporter][contact][_eq]=$CURRENT_USER&filter[bidding_status][_eq]=awarded&filter[assigned_truck][_null]=true&sort=-date_created`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch won bids');
  return json.data;
}

export async function fetchMyTrips() {
  const res = await authFetch(
    `${API_URL}/items/loads?fields=id,load_id,bidding_status,status,pickup_location,delivery_location,date_created,date_updated,assigned_truck.id,assigned_truck.truck_id,assigned_truck.status,assigned_driver.id,assigned_driver.name,order.id,order.fulfillment_status,order.batch.commodity,order.batch.grade,order.batch.quantity,bids.bid_amount,bids.status,bids.user_created&filter[awarded_transporter][contact][_eq]=$CURRENT_USER&filter[order][fulfillment_status][_in]=transport_sourced,pending_dispatch,pickup_completed,in_transit,delivered&sort=-date_created`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch trips');
  return json.data;
}

export async function assignTruckToLoad(loadId, data) {
  if (data?.fulfillment_status === 'transport_sourced' && !data?.assigned_truck) {
    throw new Error('A truck must be assigned before the order can move to Transport sourced');
  }
  const { orderId, fulfillment_status, ...loadData } = data || {};
  const res = await authFetch(`${API_URL}/items/loads/${loadId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(loadData),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to assign truck');
  if (orderId && fulfillment_status) {
    await updateOrder(orderId, { fulfillment_status });
  }
  return json.data;
}

export async function markAllNotificationsRead() {
  const res = await authFetch(
    `${API_URL}/notifications?filter[recipient][_eq]=$CURRENT_USER&filter[status][_eq]=inbox&fields=id&limit=-1`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch notifications');
  const ids = (json.data || []).map(n => n.id);
  if (ids.length === 0) return;
  const patch = await authFetch(`${API_URL}/notifications`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ keys: ids, data: { status: 'archived' } }),
  });
  if (!patch.ok) {
    const patchJson = await patch.json();
    throw new Error(patchJson?.errors?.[0]?.message || 'Failed to mark all as read');
  }
}

export async function deleteNotification(id) {
  const res = await authFetch(`${API_URL}/notifications/${id}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json?.errors?.[0]?.message || 'Failed to delete notification');
  }
}
